import _ from 'underscore';
import { useCallback, useRef } from 'react';
import { useEnvironment } from '../environment';
import {
  getRecords, getDataRef, getRecordGroup, getRecordsByEntityKey,
} from '../store';
import { specTypes } from '../spec/spec-types';
import { OVERREACT_ID_FIELD_NAME } from '../store/consts';
import { responseTypes } from '../spec/response-types';
import { OverreactRequest } from './overreact-request';
import { getMergedConfig } from './merge-config';
import { useDeepMemo } from '../utils/primitive-utilities';

import { useComponent } from './use-component';
import type {
  DataRefId,
  EntityObject,
  OverreactRequestShape,
  OverreactSpec,
  RequestConfig,
  StoredEntity,
} from '../public-types';
import type { RequestContract } from '../spec';
import type { Record as EntityRecord } from '../store/record';
import type { Store } from '../store';


const getRawData = <TEntity extends EntityObject>(data: Array<StoredEntity<TEntity>> | null) =>
  data && _.map(data, item => item.rawData);


const getDataWithOverreactIdFromRecords = <TEntity extends EntityObject>(
  records: Array<EntityRecord<TEntity> | undefined>,
) => records.filter(Boolean).map(record => ({
    rawData: record?.getData() as TEntity,
    [OVERREACT_ID_FIELD_NAME]: record?.id as string,
  }));

function mutateRecords<TEntity extends EntityObject>(

  store: Store<TEntity>,

  requestContract: RequestContract,

  recordsBeforeMutation: Array<StoredEntity<TEntity>>,

  newRecords: Array<StoredEntity<TEntity>>,
) {
  if (store) {
    const recordGroup = getRecordGroup(store, requestContract);

    recordGroup.addOrUpdateRecords(recordsBeforeMutation);
    recordGroup.addOrUpdateRecords(newRecords);
  }
}

function addRecords<TEntity extends EntityObject>(

  store: Store<TEntity>,

  requestContract: RequestContract,

  records: Array<StoredEntity<TEntity>>,
) {
  if (store && records && records.length > 0) {
    const recordGroup = getRecordGroup(store, requestContract);
    recordGroup.addOrUpdateRecords(records);

    const ids = records.map(record => record[OVERREACT_ID_FIELD_NAME]);

    // notify hooks, so that they can adjust the refIds accordingly
    recordGroup.notify('entitiesCreated', ids);
  }
}


function deleteRecords<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: RequestContract,
  records: Array<StoredEntity<TEntity>> = [],
) {
  if (store) {
    const recordGroup = getRecordGroup(store, requestContract);
    const ids = records.map(record => record[OVERREACT_ID_FIELD_NAME]);

    recordGroup.deleteRecords(ids);
    recordGroup.notify('entitiesDeleted', ids);
  }
}


function addPreemptiveRecords<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: RequestContract,
  request: OverreactRequestShape,
  records: Array<StoredEntity<TEntity>>,
) {
  if (store) {
    store.addPreemptiveRecords(request, records);
    addRecords(store, requestContract, records);
  }
}


function replacePreemptiveRecords<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: RequestContract,
  request: OverreactRequestShape,
  records: Array<StoredEntity<TEntity>>,
) {
  const preemptiveRecords = store.removePreemptiveRecords(request);
  deleteRecords(store, requestContract, preemptiveRecords);
  addRecords(store, requestContract, records);
}


function applyId<
  TEntity extends EntityObject,
  TVariables extends object & {
    locator: { descriptor: Record<string, string | number>; order?: string[] };
  },
>(
  variables: TVariables,
  spec: OverreactSpec<TVariables, unknown, TEntity | TEntity[], TEntity>,
  data: TEntity | TEntity[],
  specType: OverreactSpec['specType'],
) {
  const { locator } = variables;
  const { requestContract, responseContract } = spec;
  const { parentKeySelector } = requestContract;
  let parentId = parentKeySelector ? parentKeySelector(variables) : undefined;

  const { descriptor, order = [] } = locator;

  if (responseContract.responseType === responseTypes.COLL || specType === specTypes.ADD) {
    // when requests for COLL or create entity, the parentId is the last element in the locator
    if (!parentId && order.length > 0) {
      parentId = descriptor[order[order.length - 1]];
    }
    const dataArr = Array.isArray(data) ? data : [data];
    return dataArr.map(entity => responseContract.applyId(entity, parentId));
  }

  if (responseContract.responseType === responseTypes.ENTITY) {
    if (!parentId && order.length > 1) {
      parentId = descriptor[order[order.length - 2]];
    }

    // step 1 - generate _overreact_id
    const entity = Array.isArray(data) ? data[0] : data;
    return responseContract.applyId(entity, parentId);
  }

  throw new Error('Unknown response type, cannot apply id');
}


export interface MutationConfig<TEntity extends EntityObject, TData>
  extends RequestConfig<TEntity[]> {
  preemptiveResponseFn?: (
    _currentData: TEntity[] | TData,
    _mutationData?: TData,
  ) => TEntity | TEntity[];
}

function getPreemptiveResponseFn<TEntity extends EntityObject, TData>(
  config?: RequestConfig<TEntity | TEntity[]>,
): MutationConfig<TEntity, TData>['preemptiveResponseFn'] {
  if (config && 'preemptiveResponseFn' in config
    && typeof config.preemptiveResponseFn === 'function') {
    return config.preemptiveResponseFn as MutationConfig<TEntity, TData>['preemptiveResponseFn'];
  }
  return undefined;
}

interface MutationVariables {
  locator: { descriptor: Record<string, string | number>; order?: string[] };
}

export function useMutation<
  TEntity extends EntityObject,
  TVariables extends MutationVariables,
  TData,
>(
  dataRefId: DataRefId,
  spec: OverreactSpec<TVariables, TData, TEntity | TEntity[], TEntity>,
  config?: MutationConfig<TEntity, TData>,
) {
  // DEBUG ONLY
  const componentName = useComponent();
  const memoedConfig = useDeepMemo(config);

  const {
    requestContract,
    responseContract,
    specType,
    environmentLookupFn,
  } = spec;
  const recordsBeforeMutationRef = useRef<Array<EntityRecord<TEntity> | undefined>>();
  const environment = useEnvironment(environmentLookupFn);

  const dataCallback = useCallback((
    dataWithOverreactId: Array<StoredEntity<TEntity>> | null,
    request: OverreactRequestShape<TVariables, TData, TEntity | TEntity[], TEntity>,
  ) => {
    if (environment) {
      const store = environment.store as Store<TEntity>;
      const dataRef = getDataRef(store, requestContract, dataRefId);
      dataRef.clearError();

      const { onComplete } = request.mergedConfig || {};
      const preemptiveResponseFn = getPreemptiveResponseFn(request.mergedConfig);

      if (preemptiveResponseFn) {
        if (specType === specTypes.MUTATION) {
          const recordsBeforeMutation = recordsBeforeMutationRef.current || [];
          const dataBeforeWithId = getDataWithOverreactIdFromRecords(recordsBeforeMutation);
          mutateRecords(
            store,
            requestContract,
            dataBeforeWithId,
            dataWithOverreactId,
          );
        } else if (specType === specTypes.ADD) {
          replacePreemptiveRecords(
            store,
            requestContract,
            request,
            dataWithOverreactId,
          );
        }
      } else if (specType === specTypes.ADD) {
        addRecords(store, requestContract, dataWithOverreactId);
      } else if (specType === specTypes.MUTATION) {
        mutateRecords(
          store,
          requestContract,
          [],
          dataWithOverreactId,
        );
      }

      if (specType === specTypes.DELETE) {
        const { data } = request;
        const dataArray = Array.isArray(data) ? data : [data];
        const { keySelector } = responseContract;
        const keys = dataArray.map(d => keySelector(d));
        const records = getRecordsByEntityKey(store, spec, keys);
        const dataWithId = getDataWithOverreactIdFromRecords(records);

        deleteRecords(store, requestContract, dataWithId);
      }

      const data = getRawData(dataWithOverreactId);

      if (onComplete) {
        onComplete(data);
      }
    }
  }, [dataRefId, environment, requestContract, responseContract, spec, specType]);

  const errorCallback = useCallback((
    error: unknown,
    request: OverreactRequestShape<TVariables, TData, TEntity | TEntity[], TEntity>,
  ) => {
    if (environment) {
      const store = environment.store as Store<TEntity>;
      const { onError } = request.mergedConfig || {};
      const preemptiveResponseFn = getPreemptiveResponseFn(request.mergedConfig);


      const recordsBeforeMutation = recordsBeforeMutationRef.current || [];
      const dataBeforeWithId = getDataWithOverreactIdFromRecords(recordsBeforeMutation);

      if (preemptiveResponseFn) {
        if (specType === specTypes.MUTATION) {
          mutateRecords(
            store,
            requestContract,
            dataBeforeWithId,
            [],
          );
        } else if (specType === specTypes.ADD) {
          replacePreemptiveRecords(
            store,
            requestContract,
            request,
            [],
          );
        }
      }

      if (onError) {
        onError(error);
      }
    }
  }, [environment, requestContract, specType]);

  const mutateFn = useCallback((
    variables: TVariables,
    mutationData: TData,
    ...rest: Array<Partial<MutationConfig<TEntity, TData>>>
  ) => {
    if (environment) {
      const store = environment.store as Store<TEntity>;
      const requestConfig = rest.slice(-1)[0];
      const mergedConfig = getMergedConfig(requestConfig, memoedConfig);
      const { preemptiveResponseFn } = mergedConfig;

      // stash current value. This will be useful when preemptive updates is enabled,
      // and we need to revert to original state before applying actual response from server.
      if (dataRefId) {
        const recordsBeforeMutation = getRecords(store, requestContract, dataRefId);
        if (recordsBeforeMutation) {
          recordsBeforeMutationRef.current = recordsBeforeMutation;
        }
      }

      const request = new OverreactRequest({
        id: dataRefId,
        requestContract,
        spec,
        variables,
        data: mutationData,
        dataCb: dataCallback,
        errorCb: errorCallback,
        mergedConfig,
        componentName,
      });

      // we can perform preemptive updates right now (before actual request)
      // the store will be reverted/merged with response in data callbacks - when the response
      // comes back.
      if (preemptiveResponseFn) {
        if (specType === specTypes.MUTATION) {
          const recordsBeforeMutation = recordsBeforeMutationRef.current || [];
          const dataBeforeWithId = getDataWithOverreactIdFromRecords(recordsBeforeMutation);
          const dataBefore = _.map(dataBeforeWithId, d => d.rawData);
          const preemptiveData = preemptiveResponseFn(dataBefore, mutationData);

          const { keySelector } = responseContract;
          const preemptiveItems = Array.isArray(preemptiveData)
            ? preemptiveData
            : [preemptiveData];
          const dataWithOverreactId = preemptiveItems.map(d => {
            const key = keySelector(d);
            const [record] = getRecordsByEntityKey(store, spec, [key]);
            return {
              rawData: d,
              [OVERREACT_ID_FIELD_NAME]: record.id,
            };
          });

          mutateRecords(
            store,
            requestContract,
            dataBeforeWithId,
            dataWithOverreactId,
          );
        } else if (specType === specTypes.ADD) {
          const data = preemptiveResponseFn(mutationData);

          const appliedIds = applyId(request.variables, spec, data, specType);
          const records = Array.isArray(appliedIds) ? appliedIds : [appliedIds];
          addPreemptiveRecords(
            store,
            requestContract,
            request,
            records,
          );
        }
      }

      // Register the request to be issued - in this implementation the request will go to the
      // request queue in DataFetcher, waiting to be invoked. So one shall not assume
      // the request will go out immediately.
      environment.pushRequest(request);
    }
  }, [
    componentName,
    dataCallback,
    dataRefId,
    environment,
    errorCallback,
    memoedConfig,
    requestContract,
    responseContract,
    spec,
    specType,
  ]);

  return mutateFn;
}
